import { throttle } from "lodash";
import React, { useContext, useEffect, useState } from "react";
import { HeaderHeightContext } from "@/app/providers/navigationProvider";
import { Category } from "@/dto/category";
import { UNCATEGORISED_CATEGORY_ID } from "@/utils/constants";

const EMPTY_CATEGORY_LISTS: Category[][] = [];

const getCategoryData = (
  category: Element,
  categoryLists: Category[][]
): Category | undefined => {
  const categoryName = category.id;
  return categoryLists.flat().find(
    (categoryItem) =>
      categoryItem.id === category.id || categoryItem.name === categoryName
  );
};

const getCategorySortOrder = (
  category: HTMLElement,
  categoryData: Category | undefined,
  index: number
): number => {
  const rawSortOrder = Number(category.dataset.sortOrder);

  if (categoryData?.id === UNCATEGORISED_CATEGORY_ID) {
    return Number.MAX_SAFE_INTEGER;
  }

  if (Number.isFinite(rawSortOrder)) {
    return rawSortOrder;
  }

  return categoryData?.sortOrder ?? index;
};

const getCategoryPosition = (
  category: Element,
  index: number,
  categoryLists: Category[][]
) => {
  const heading = category.querySelector("h2") ?? category;
  const rect = category.getBoundingClientRect();
  const headingRect = heading.getBoundingClientRect();
  const categoryData = getCategoryData(category, categoryLists);

  return {
    category,
    categoryData,
    headingRect,
    index,
    rect,
    sortOrder: getCategorySortOrder(category as HTMLElement, categoryData, index),
  };
};

const calculateCategoryInView = (
  categories: NodeListOf<Element>,
  categoryLists: Category[][],
  headerBottom: number,
  windowHeight: number,
  scrollingUp: boolean
): string | null => {
  if (categories.length === 0) return null;

  const visibleCategories = Array.from(categories)
    .map((category, index) =>
      getCategoryPosition(category, index, categoryLists)
    )
    .filter(
      ({ rect }) => rect.bottom > headerBottom && rect.top < windowHeight
    )
    .sort((a, b) => a.sortOrder - b.sortOrder || a.index - b.index);

  const categoryAtBoundary = visibleCategories.filter(
    ({ headingRect }) => headingRect.top <= headerBottom
  );
  const categoryEnteringFromBelow = visibleCategories.find(
    ({ headingRect }) =>
      headingRect.top > headerBottom && headingRect.top < windowHeight
  );
  let selectedCategory: string | undefined;

  if (scrollingUp) {
    selectedCategory = categoryEnteringFromBelow?.category.id;

    if (!selectedCategory) {
      selectedCategory = categoryAtBoundary.at(-1)?.category.id;
    }
  } else {
    selectedCategory = categoryAtBoundary.at(-1)?.category.id;

    if (!selectedCategory) {
      selectedCategory = categoryEnteringFromBelow?.category.id;
    }
  }

  return selectedCategory ?? null;
};

export function useCategoryInView(
  categoryLists: Category[][] = EMPTY_CATEGORY_LISTS
): [
  string | null | undefined,
  React.Dispatch<React.SetStateAction<string | null | undefined>>
] {
  const [categoryInView, setCategoryInView] = useState<string | null>();
  const headerContext = useContext(HeaderHeightContext);

  useEffect(() => {
    const mainElement = document?.querySelector("main");
    let previousScrollTop = mainElement?.scrollTop ?? 0;

    const handleScroll = throttle(() => {
      const scrollTop = mainElement?.scrollTop ?? 0;
      const scrollingUp = scrollTop < previousScrollTop;
      previousScrollTop = scrollTop;
      const categories = document?.querySelectorAll(".category");
      const header = document?.querySelector("header");
      const headerRect =
        headerContext?.headerContentRef.current?.getBoundingClientRect() ??
        header?.getBoundingClientRect();
      let headerBottom = headerContext?.headerHeight ?? 0;

      if (headerRect) {
        if (Number.isFinite(headerRect.bottom)) {
          headerBottom = headerRect.bottom;
        } else {
          headerBottom = headerRect.height;
        }
      }
      const windowHeight =
        window?.innerHeight || document?.documentElement?.clientHeight || 0;
      const currentCategory = calculateCategoryInView(
        categories ?? [],
        categoryLists,
        headerBottom,
        windowHeight,
        scrollingUp
      );
      setCategoryInView(() => currentCategory);
    }, 50);
    // container with scrollable content
    mainElement?.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      mainElement?.removeEventListener("scroll", handleScroll);
      handleScroll.cancel();
    };
  }, [categoryLists, headerContext]);

  return [categoryInView, setCategoryInView];
}
